import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeToggle } from "./ui/ThemeToggle";
import { Menu, X } from 'lucide-react';
// import { Link } from 'react-scroll'; // Removed unused import
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
// import QuoteModal from '@/components/QuoteModal'; // Corrected import path

interface NavLinkProps {
  href: string;
  children: React.ReactNode;
  onClick?: () => void;
  className?: string; // Add className to props
}

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  // Quote Modal State
  // const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      // Adjust rootMargin to cover more of the viewport vertically
      rootMargin: '-50% 0px -50% 0px', // This makes the center of the viewport the trigger point
      threshold: 0, // Trigger as soon as any part of the section is visible
    };

    const observerCallback = (entries) => {
      // Find the entry that is currently intersecting with the root (viewport center)
      const intersectingEntry = entries.find(entry => entry.isIntersecting);
      
      if (intersectingEntry) {
        setActiveSection(intersectingEntry.target.id);
      }
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all sections
    const sections = document.querySelectorAll('section[id], div[id]'); // Also observe divs with id like 'hero'
    sections.forEach((section) => {
      observer.observe(section);
    });

    // No need to observe hero separately if it's now included in the query
    // const heroSection = document.getElementById('hero');
    // if (heroSection) {
    //   observer.observe(heroSection);
    // }

    return () => {
      sections.forEach((section) => {
        observer.unobserve(section);
      });
      // if (heroSection) {
      //   observer.unobserve(heroSection);
      // }
    };
  }, []);

  const navItems = [
    { name: "Home", href: "hero" },
    { name: "About Us", href: "about" },
    { name: "Services", href: "services" },
    { name: "Founder", href: "founder" },
    { name: "Media Partners", href: "media-partners" },
    { name: "Blog", href: "blog" },
    { name: "Contact", href: "contact" },
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Adjusting scroll position to account for the fixed navbar
      const navbar = document.querySelector('nav');
      const navbarHeight = navbar ? navbar.offsetHeight : 0;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - navbarHeight,
        behavior: 'smooth'
      });
      // Close mobile menu after clicking a link
      setIsMobileMenuOpen(false);
      // The IntersectionObserver will handle setting the active section after scroll
      // setActiveSection(sectionId);
    }
  };

  // Desktop NavLink styling (original smaller size)
  const NavLink: React.FC<NavLinkProps> = ({ href, children, onClick = () => {}, className }) => (
    <motion.a
      href={`#${href}`}
      onClick={onClick}
      className={className || "text-sm font-medium text-muted-foreground transition-colors hover:text-primary py-2 px-3 rounded-md"}
      whileHover={{ y: -2 }}
    >
      {children}
    </motion.a>
  );

  // const handleOpenQuoteModal = () => setIsQuoteModalOpen(true);
  // const handleCloseQuoteModal = () => setIsQuoteModalOpen(false);

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-background/80 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, delay: 2.5 }} // Delay to match loading sequence
    >
      <div className="w-full px-6">
        <div className="flex justify-between items-center h-20">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
          >
            <img 
              src="/images/logo.webp" 
              alt="MCubes" 
              className="h-8"
            />
          </motion.div>

          <div className="hidden lg:flex space-x-1">
            {navItems.map((item) => (
              <NavLink 
                key={item.name} 
                href={item.href} 
                // Conditional class: white at top, muted-foreground when scrolled
                className={`${!isScrolled ? 'text-white' : 'text-muted-foreground'} transition-colors hover:text-primary text-sm font-medium py-2 px-3 rounded-md`}
              >
                {item.name}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center space-x-4">
            <ThemeToggle />
            {/* CTA Button to open Quote Modal */}
            <Button 
              variant="outline" 
              className="hidden lg:flex bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={() => scrollToSection('contact')}
            >
              Get a Quote
            </Button>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" className={`${!isScrolled ? 'text-white' : 'text-foreground'}`} aria-label={isOpen ? "Close menu" : "Open menu"}>
                  <Menu className="h-12 w-12" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              {/* Mobile Sheet Content (Full screen, larger links) */}
              <SheetContent side="right" className="w-full bg-background p-6 flex flex-col items-center justify-center">
                <div className="absolute top-4 right-4">
                  <motion.button
                    onClick={() => setIsOpen(false)}
                    className="p-2 rounded-full hover:bg-accent text-muted-foreground hover:text-accent-foreground transition-colors"
                    aria-label="Close menu"
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X className="h-12 w-12" />
                    <span className="sr-only">Close</span>
                  </motion.button>
                </div>
                <div className="flex flex-col space-y-6 mt-8 w-full">
                  {navItems.map((item) => (
                    <NavLink 
                      key={item.name} 
                      href={item.href} 
                      onClick={() => setIsOpen(false)}
                      // Apply mobile specific styles directly to the instance in the sheet
                      className="text-lg font-medium text-foreground transition-colors hover:text-primary py-4 px-3 rounded-md w-full text-center"
                    >
                      {item.name}
                    </NavLink>
                  ))}
                  <Button variant="default" className="w-full" onClick={() => { scrollToSection('contact'); }}>
                    Get a Quote
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navigation;

